<template>
    <div class="form-control datepicker-input">
        <i v-if="mobileIcon" class="mdi mdi-calendar datepicker-icon" @click="openDatePicker"></i>
        <input
            class="datepicker-input"
            :disabled="disabled"
            :class="{ '!cursor-not-allowed': disabled }"
            :value="modelValue"
            ref="datePicker"
            type="text"
            v-bind="$attrs"
            :placeholder="disablePlaceholder ? null : placeholder"
        />
    </div>
</template>

<script>
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

export default {
    inheritAttrs: false,
    props: {
        config: {
            type: Object,
            default: null,
        },
        mobileIcon: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            required: true,
            default: null,
            validator(value) {
                return (
                    value === null ||
                    value instanceof Date ||
                    typeof value === 'string' ||
                    value instanceof String
                );
            },
        },
        placeholder: {
            type: String,
            default: () => {
                const now = new Date(),
                    day = now.getDate(),
                    month = now.getMonth() + 1,
                    year = now.getFullYear();

                return (day > 9 ? '' : '0') + day + '.' + (month > 9 ? '' : '0') + month + '.' + year;
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dateFormat: {
            type: String,
            default: 'd.m.Y',
        },
        twelveHourTime: {
            type: Boolean,
            default: false,
        },
        enableTime: {
            type: Boolean,
            default: false,
        },
        enableSeconds: {
            type: Boolean,
            default: false,
        },
        firstDayOfWeek: {
            type: Number,
            default: 1,
        },
        disablePlaceholder: {
            type: Boolean,
            default: false,
        },
    },

    watch: {
        /**
         * Watch for any change in options and set them
         *
         * @param newConfig Object
         */
        config: {
            deep: true,
            handler(newConfig) {
                if (this.flatpickr) {
                    this.flatpickr.set('minDate', newConfig.minDate);
                }
            },
        },
    },

    data: () => ({ flatpickr: null }),

    mounted() {
        this.$nextTick(() => {
            const flatpickrConfig = {
                minDate: this.config.minDate,
                maxDate: this.config.maxDate ?? [],
                enableTime: this.enableTime,
                enableSeconds: this.enableSeconds,
                onClose: this.onClose,
                onChange: this.onChange,
                dateFormat: this.dateFormat,
                allowInput: true,
                time_24hr: !this.twelveHourTime,
                locale: this.getLocale(),
            };
            if (this.config.enabledDates) {
                flatpickrConfig.enable = this.config.enabledDates;
            }
            this.flatpickr = flatpickr(this.$refs.datePicker, flatpickrConfig);
            if (!this.disablePlaceholder) {
                // If placeholder is disabled this will prevent the mobile input from showing preselected values.
                this.flatpickr.setDate(this.config.minDate, true);
            }
        });
    },

    methods: {
        openDatePicker() {
            if (this.flatpickr && !this.disabled) {
                this.flatpickr.open();
            }
        },

        setDate(date) {
            if (!this.disablePlaceholder) {
                // If placeholder is disabled this will prevent the mobile input from showing preselected values.
                this.flatpickr.setDate(date, true);
                this.$forceUpdate();
            }
        },

        setMinDate(minDate) {
            this.$nextTick(() => {
                this.flatpickr.set('minDate', minDate);
                this.$forceUpdate();
            });
        },

        setMaxDate(maxDate) {
            this.$nextTick(() => {
                this.flatpickr.set('maxDate', maxDate);
                this.$forceUpdate();
            });
        },

        getLocale() {
            // get the locale
            const locale = this.$i18n.messages[this.$i18n.locale].flatpickr;
            // build the locale
            return {
                weekdays: {
                    shorthand: [
                        locale.shorthand_sunday,
                        locale.shorthand_monday,
                        locale.shorthand_tuesday,
                        locale.shorthand_wednesday,
                        locale.shorthand_thursday,
                        locale.shorthand_friday,
                        locale.shorthand_saturday,
                    ],
                    longhand: [
                        locale.longhand_sunday,
                        locale.longhand_monday,
                        locale.longhand_tuesday,
                        locale.longhand_wednesday,
                        locale.longhand_thursday,
                        locale.longhand_friday,
                        locale.longhand_saturday,
                    ],
                },

                months: {
                    shorthand: [
                        locale.shorthand_january,
                        locale.shorthand_february,
                        locale.shorthand_march,
                        locale.shorthand_april,
                        locale.shorthand_may,
                        locale.shorthand_june,
                        locale.shorthand_july,
                        locale.shorthand_august,
                        locale.shorthand_september,
                        locale.shorthand_october,
                        locale.shorthand_november,
                        locale.shorthand_december,
                    ],
                    longhand: [
                        locale.longhand_january,
                        locale.longhand_february,
                        locale.longhand_march,
                        locale.longhand_april,
                        locale.longhand_may,
                        locale.longhand_june,
                        locale.longhand_july,
                        locale.longhand_august,
                        locale.longhand_september,
                        locale.longhand_october,
                        locale.longhand_november,
                        locale.longhand_december,
                    ],
                },
                firstDayOfWeek: locale.first_day_of_week,
                weekAbbreviation: locale.week_abbreviation,
                rangeSeparator: locale.range_separator,
                time_24hr: 'true' === locale.time_24hr,
            };
        },

        onClose() {
            this.$emit('close', this.$refs.datePicker.value);
            this.onChange();
        },

        onChange() {
            if (
                this.flatpickr.isMobile &&
                this.flatpickr.latestSelectedDateObj === undefined
            ) {
                // The latestSelectedDateObj from flatpickr is undefined,
                // if a date got selected, which was not initialized in flatpickr.
                // This could mean, that the user selected a date, which is not available.
                const minDate = new Date(this.config.minDate);
                this.$emit('dp-invalid-date', {
                    purchaseAbleDate: minDate.toLocaleDateString(
                        'de-DE',
                        {year: 'numeric', month: '2-digit', day:'2-digit'}
                    ),
                });
            }

            this.$emit('input', this.$refs.datePicker.value);
            this.$emit('change', this.$refs.datePicker.value);
            this.$emit('dp-change', this.$refs.datePicker.value);
            this.$emit('update:model-value', this.$refs.datePicker.value);
        },
    },
};
</script>

<style scoped>
.\!cursor-not-allowed {
    cursor: not-allowed !important;
}

input.datepicker-input {
    border: none;
    caret-color: #161c2d;
    width: 100%;
    height: 100%;
    min-width: 100px;
    word-wrap: normal;
}

.datepicker-overlay .datepicker-input {
    word-wrap: normal;
}
</style>
